import type { CSSProperties, ReactNode } from 'react'

interface ProjectHeroBackgroundProps {
  thumbUrl: string | null
  /** Stable id used to derive the styled-empty background color when there's no hero. */
  fallbackSeed: string
  children: ReactNode
}

/**
 * Phase 8 — `BrowseProjectCard` background. Renders the hero thumb with a dark gradient overlay
 * for legibility; falls back to a deterministic styled-empty background when there's no hero.
 */
export const ProjectHeroBackground = ({
  thumbUrl,
  fallbackSeed,
  children,
}: ProjectHeroBackgroundProps) => {
  const style: CSSProperties = thumbUrl
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.6) 100%), url(${thumbUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        backgroundColor: fallbackColor(fallbackSeed),
      }
  return (
    <div
      data-testid="project-hero-background"
      style={style}
      className={
        'flex flex-col justify-end gap-1 p-4 ' +
        (thumbUrl ? 'min-h-[140px] text-white' : 'min-h-[140px] text-foreground')
      }
    >
      {children}
    </div>
  )
}

const fallbackColor = (seed: string): string => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 30%, 90%)`
}
